(function () {
  'use strict';

  const sId        = window.__sparkSectionId || '';
  const CM_TO_INCH = 1 / 2.54;
  const MARGIN_CM  = 2; // 2 cm margin for flags (tucked behind hanging rod / hem)

  // ── State ──────────────────────────────────────────────────────────────────
  let lastURL        = null;
  let originalFile   = null;
  let storedW        = null;
  let storedH        = null;
  let cropperInst    = null;
  let croppedDataURL = null;
  let lastDpi        = null;
  let lastTier       = null;
  let lastCropData   = null;
  let dimsRotated    = false;
  let processing     = false;

  function g(id) { return document.getElementById(id); }

  // ── DIMENSION HELPERS ──────────────────────────────────────────────────────
  function getPrintDims() {
    for (const fs of document.querySelectorAll('fieldset')) {
      const legend = fs.querySelector('legend');
      if (legend && legend.textContent.toLowerCase().includes('size')) {
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (checked) {
          const nums = checked.value.match(/\d+(\.\d+)?/g);
          if (nums && nums.length >= 2) return { w: parseFloat(nums[0]), h: parseFloat(nums[1]) };
        }
      }
    }
    for (const sel of document.querySelectorAll('select')) {
      const wrap = sel.closest('.product-form__input');
      const lbl  = wrap && wrap.querySelector('.form__label, label');
      if (lbl && lbl.textContent.toLowerCase().includes('size')) {
        const nums = (sel.options[sel.selectedIndex]?.text || '').match(/\d+(\.\d+)?/g);
        if (nums && nums.length >= 2) return { w: parseFloat(nums[0]), h: parseFloat(nums[1]) };
      }
    }
    const checked = document.querySelector('input[type="radio"]:checked');
    if (checked) {
      const nums = checked.value.match(/\d+(\.\d+)?/g);
      if (nums && nums.length >= 2) return { w: parseFloat(nums[0]), h: parseFloat(nums[1]) };
    }
    return null;
  }

  function getEffectiveDims() {
    const d = getPrintDims();
    if (!d) return null;
    return dimsRotated ? { w: d.h, h: d.w } : d;
  }

  function calcDpi(cropData, dims) {
    if (!cropData || !dims || !cropData.width || !cropData.height) return null;
    const printLong  = Math.max(dims.w, dims.h) * CM_TO_INCH;
    const printShort = Math.min(dims.w, dims.h) * CM_TO_INCH;
    const cropLong   = Math.max(cropData.width, cropData.height);
    const cropShort  = Math.min(cropData.width, cropData.height);
    return Math.min(cropLong / printLong, cropShort / printShort);
  }

  function dpiTier(dpi) {
    if (!dpi)       return 'bad';
    if (dpi >= 120) return 'excellent';
    if (dpi >= 25)  return 'good';
    return 'bad';
  }

  function tierLabel(tier) {
    return { excellent: 'Excellent', good: 'Good', bad: 'Bad' }[tier] || tier;
  }

  // ── PROCESSING LOCK ────────────────────────────────────────────────────────
  function setProcessing(on) {
    processing = on;
    ['sparkMockupRotate-', 'sparkMockupEdit-'].forEach(function (prefix) {
      const el = g(prefix + sId);
      if (!el) return;
      el.disabled      = on;
      el.style.opacity = on ? '0.45' : '';
      el.style.cursor  = on ? 'wait'  : '';
    });
  }

  // ── AUTO CROP (center) ─────────────────────────────────────────────────────
  function autoCropToCanvas(imgW, imgH, ratio) {
    let srcX = 0, srcY = 0, srcW = imgW, srcH = imgH;
    const imgRatio = imgW / imgH;
    if (imgRatio > ratio) { srcW = Math.round(imgH * ratio);  srcX = Math.round((imgW - srcW) / 2); }
    else                  { srcH = Math.round(imgW / ratio);  srcY = Math.round((imgH - srcH) / 2); }
    const canvas = document.createElement('canvas');
    canvas.width  = srcW;
    canvas.height = srcH;
    return new Promise(function (resolve) {
      const img = new Image();
      img.onload = function () {
        canvas.getContext('2d').drawImage(img, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
        lastCropData = { x: srcX, y: srcY, width: srcW, height: srcH, rotate: 0, scaleX: 1, scaleY: 1 };
        resolve({ canvas: canvas, cropData: lastCropData });
      };
      img.src = lastURL;
    });
  }

  // ── PRESERVE-CROP RE-CROP ──────────────────────────────────────────────────
  function reCropToRatio(dims) {
    return new Promise(function (resolve) {
      const ratio = dims.w / dims.h;
      if (!lastCropData || !lastCropData.width) {
        autoCropToCanvas(storedW, storedH, ratio).then(resolve);
        return;
      }
      const cx = lastCropData.x + lastCropData.width  / 2;
      const cy = lastCropData.y + lastCropData.height / 2;
      let newW, newH;
      if (storedW / storedH > ratio) { newH = storedH; newW = Math.round(newH * ratio); }
      else                           { newW = storedW; newH = Math.round(newW / ratio); }
      const newX = Math.max(0, Math.min(Math.round(cx - newW / 2), storedW - newW));
      const newY = Math.max(0, Math.min(Math.round(cy - newH / 2), storedH - newH));
      const cropCanvas = document.createElement('canvas');
      cropCanvas.width  = newW;
      cropCanvas.height = newH;
      const img = new Image();
      img.onload = function () {
        cropCanvas.getContext('2d').drawImage(img, newX, newY, newW, newH, 0, 0, newW, newH);
        lastCropData = { x: newX, y: newY, width: newW, height: newH, rotate: 0, scaleX: 1, scaleY: 1 };
        resolve({ canvas: cropCanvas, cropData: lastCropData });
      };
      img.src = lastURL;
    });
  }

  // ── SAFE-ZONE PREVIEW CANVAS ───────────────────────────────────────────────
  function makeSafeZoneCanvas(fullCanvas, dims) {
    if (!dims) return fullCanvas;
    var fw = fullCanvas.width, fh = fullCanvas.height;
    var mX = Math.round((MARGIN_CM / dims.w) * fw);
    var mY = Math.round((MARGIN_CM / dims.h) * fh);
    var sw = fw - mX * 2, sh = fh - mY * 2;
    if (sw <= 0 || sh <= 0) return fullCanvas;
    var c = document.createElement('canvas');
    c.width = sw; c.height = sh;
    c.getContext('2d').drawImage(fullCanvas, mX, mY, sw, sh, 0, 0, sw, sh);
    return c;
  }

  // ── SIZE SUGGESTION ────────────────────────────────────────────────────────
  function suggestAndSelectBestSize(imgW, imgH) {
    const imgRatio = imgW / imgH;
    const sizeRadios = [];
    for (const fs of document.querySelectorAll('fieldset')) {
      const legend = fs.querySelector('legend');
      if (legend && legend.textContent.toLowerCase().includes('size')) {
        fs.querySelectorAll('input[type="radio"]').forEach(function (r) { sizeRadios.push(r); });
      }
    }
    if (!sizeRadios.length) {
      document.querySelectorAll('input[type="radio"]').forEach(function (r) {
        const nums = r.value.match(/\d+(\.\d+)?/g);
        if (nums && nums.length >= 2) sizeRadios.push(r);
      });
    }
    let sizeSelect = null;
    for (const sel of document.querySelectorAll('select')) {
      const wrap = sel.closest('.product-form__input');
      const lbl  = wrap && wrap.querySelector('.form__label, label');
      if (lbl && lbl.textContent.toLowerCase().includes('size')) { sizeSelect = sel; break; }
    }
    const candidates = [];
    sizeRadios.forEach(function (r) {
      const nums = r.value.match(/\d+(\.\d+)?/g);
      if (nums && nums.length >= 2) {
        const w = parseFloat(nums[0]), h = parseFloat(nums[1]);
        if (w > 0 && h > 0) candidates.push({ el: r, ratio: w / h, label: r.value, isSelect: false });
      }
    });
    if (sizeSelect) {
      Array.from(sizeSelect.options).forEach(function (opt, idx) {
        if (!opt.value) return;
        const nums = (opt.text || opt.value).match(/\d+(\.\d+)?/g);
        if (nums && nums.length >= 2) {
          const w = parseFloat(nums[0]), h = parseFloat(nums[1]);
          if (w > 0 && h > 0) candidates.push({ el: sizeSelect, optionIndex: idx, ratio: w / h, label: opt.text, isSelect: true });
        }
      });
    }
    if (!candidates.length) return;
    let best = null, bestScore = Infinity;
    candidates.forEach(function (c) {
      const scoreA = Math.abs(Math.log(imgRatio / c.ratio));
      const scoreB = Math.abs(Math.log(imgRatio / (1 / c.ratio)));
      const score  = Math.min(scoreA, scoreB);
      if (score < bestScore) { bestScore = score; best = c; }
    });
    if (!best) return;
    if (best.isSelect) { best.el.selectedIndex = best.optionIndex; }
    else               { best.el.checked = true; }
    showSizeToast(best.label);
    best.el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function showSizeToast(label) {
    var existing = g('sparkSizeToast-' + sId);
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.id = 'sparkSizeToast-' + sId;
    toast.style.cssText =
      'position:fixed;bottom:24px;left:50%;transform:translateX(-50%) translateY(16px);' +
      'background:#242833;color:#F29F05;padding:12px 18px;border-radius:10px;font-size:13px;' +
      'font-weight:500;z-index:9999;box-shadow:0 4px 20px rgba(0,0,0,0.35);' +
      'opacity:0;transition:opacity 0.25s,transform 0.25s;pointer-events:none;' +
      'max-width:320px;text-align:center;line-height:1.5;';
    toast.innerHTML =
      '<div style="font-size:13px;font-weight:700;margin-bottom:3px;color:#F29F05;">✦ Best size preselected: ' + label + '</div>' +
      '<div style="font-size:11.5px;color:rgba(242,159,5,0.65);font-weight:400;">Choosing a different size may require re-cropping your photo.</div>';
    document.body.appendChild(toast);
    requestAnimationFrame(function () {
      toast.style.opacity   = '1';
      toast.style.transform = 'translateX(-50%) translateY(0)';
    });
    setTimeout(function () {
      toast.style.opacity   = '0';
      toast.style.transform = 'translateX(-50%) translateY(16px)';
      setTimeout(function () { if (toast.parentNode) toast.remove(); }, 300);
    }, 5000);
  }

  // ── GALLERY DETECTION ──────────────────────────────────────────────────────
  function getGalleryEl() {
    const mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery) return mediaGallery;
    const slide = document.querySelector('.product__media.media.media--transparent');
    if (!slide) return null;
    const candidates = ['media-gallery','slider-component','.product__media-wrapper','.product__media-list','.product-media-container'];
    for (const sel of candidates) {
      const el = slide.closest(sel);
      if (el) return el;
    }
    return slide.parentNode || slide;
  }

  // ── FLAG SVG MOCKUP ────────────────────────────────────────────────────────
  function buildFlagSVG(dataURL, ratio) {
    var vw = 100, vh = Math.round(100 / ratio);
    var dip = Math.round(vh * 0.045), side = Math.round(vh * 0.025);
    var path = 'M 0,0 Q 50,' + dip + ' 100,0 L ' + (100 - side) + ',' + vh + ' L ' + side + ',' + vh + ' Z';
    var clipId = 'sparkFlagClip-' + sId, gradH = 'sparkFlagGradH-' + sId, gradT = 'sparkFlagGradT-' + sId;
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + vw + ' ' + vh + '" ' +
           'style="display:block;width:100%;height:auto;filter:drop-shadow(0 10px 28px rgba(0,0,0,0.5)) drop-shadow(0 2px 6px rgba(0,0,0,0.25));">' +
        '<defs>' +
          '<clipPath id="' + clipId + '"><path d="' + path + '"/></clipPath>' +
          '<linearGradient id="' + gradH + '" x1="0" y1="0" x2="1" y2="0">' +
            '<stop offset="0%" stop-color="#000" stop-opacity="0.2"/><stop offset="10%" stop-color="#000" stop-opacity="0"/>' +
            '<stop offset="90%" stop-color="#000" stop-opacity="0"/><stop offset="100%" stop-color="#000" stop-opacity="0.2"/>' +
          '</linearGradient>' +
          '<linearGradient id="' + gradT + '" x1="0" y1="0" x2="0" y2="1">' +
            '<stop offset="0%" stop-color="#000" stop-opacity="0.25"/><stop offset="10%" stop-color="#000" stop-opacity="0"/>' +
          '</linearGradient>' +
        '</defs>' +
        '<image href="' + dataURL + '" x="0" y="0" width="' + vw + '" height="' + vh + '" preserveAspectRatio="xMidYMid slice" clip-path="url(#' + clipId + ')"/>' +
        '<path d="' + path + '" fill="url(#' + gradH + ')" clip-path="url(#' + clipId + ')"/>' +
        '<path d="' + path + '" fill="url(#' + gradT + ')" clip-path="url(#' + clipId + ')"/>' +
      '</svg>'
    );
  }

  // ── MOCKUP ─────────────────────────────────────────────────────────────────
  function injectMockupPlaceholder() {
    if (g('sparkMockupWrap-' + sId)) return;
    const gallery = getGalleryEl();
    if (!gallery) return;
    const wrap = document.createElement('div');
    wrap.id        = 'sparkMockupWrap-' + sId;
    wrap.className = 'spark-mockup-wrap';
    wrap.style.cssText = 'display:block; width:90%; max-width:480px; margin-left:auto; margin-right:auto; position:relative;';
    wrap.innerHTML =
      '<div id="sparkUploadPrompt-' + sId + '" class="spark-upload-prompt">' +
        '<svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color:#ccc;margin-bottom:10px;"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>' +
        '<p style="margin:0 0 14px;font-size:14px;font-weight:600;color:#555;">Upload your photo to see a preview</p>' +
        '<label for="file-upload-input-' + sId + '" class="spark-upload-btn" style="cursor:pointer;">Choose Photo</label>' +
      '</div>' +
      '<div id="sparkMockupFrame-' + sId + '" style="display:none; overflow:visible;"></div>' +
      '<div id="sparkMockupToolbar-' + sId + '" class="spark-mockup-actions" style="display:none;">' +
        '<button class="spark-mockup-btn smb-primary" id="sparkMockupEdit-' + sId + '" type="button"><span class="smb-icon">✎</span><span class="smb-label">Crop</span></button>' +
        '<button class="spark-mockup-btn" id="sparkMockupRotate-' + sId + '" type="button">' +
          '<span class="smb-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6"/><path d="M21.34 15.57a10 10 0 1 1-.57-8.38"/></svg></span>' +
          '<span class="smb-label">Rotate</span></button>' +
        '<button class="spark-mockup-btn smb-reset" id="sparkMockupReset-' + sId + '" type="button">' +
          '<span class="smb-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20H7L3 16l9.5-9.5 5 5L20 20z"/><path d="M6.5 17.5l-2-2"/><path d="M15 7l2-2 2 2-2 2-2-2z"/></svg></span>' +
          '<span class="smb-label">Reset</span></button>' +
        '<button class="spark-mockup-btn smb-remove" id="sparkMockupRemove-' + sId + '" type="button">' +
          '<span class="smb-icon"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></span>' +
          '<span class="smb-label">Remove Image</span></button>' +
      '</div>' +
      '<div id="qBadge-' + sId + '" class="quality-badge" style="display:none; margin-top:8px;"></div>' +
      '<p class="spark-mockup-label" id="sparkMockupLabel-' + sId + '" style="display:none;"></p>';

    gallery.parentNode.insertBefore(wrap, gallery);
    gallery.style.display = 'none';

    g('sparkMockupEdit-'   + sId).addEventListener('click', function () { openCropperModal(lastURL, true); });
    g('sparkMockupRotate-' + sId).addEventListener('click', onRotate);
    g('sparkMockupRemove-' + sId).addEventListener('click', resetAll);
    g('sparkMockupReset-'  + sId).addEventListener('click', function () {
      if (!originalFile) return;
      dimsRotated = false; lastCropData = null;
      if (lastURL) URL.revokeObjectURL(lastURL);
      lastURL = URL.createObjectURL(originalFile);
      storedW = null; storedH = null;
      const tempImg = new Image();
      tempImg.onload = function () {
        storedW = tempImg.naturalWidth; storedH = tempImg.naturalHeight;
        const rawDims = getPrintDims();
        if (rawDims) dimsRotated = (storedH > storedW) !== (rawDims.h > rawDims.w);
        const dims = getEffectiveDims();
        if (dims) {
          autoCropToCanvas(storedW, storedH, dims.w / dims.h).then(function (result) {
            generatePreviewAndCommit(result.canvas, result.cropData, calcDpi(result.cropData, dims), dpiTier(calcDpi(result.cropData, dims)), false);
          });
        }
      };
      tempImg.src = lastURL;
    });
  }

  function showMockup(dataURL) {
    injectMockupPlaceholder();
    const gallery = getGalleryEl(); if (gallery) gallery.style.display = 'none';
    const prompt  = g('sparkUploadPrompt-'  + sId);
    const frame   = g('sparkMockupFrame-'   + sId);
    const toolbar = g('sparkMockupToolbar-' + sId);
    const label   = g('sparkMockupLabel-'   + sId);
    const badge   = g('qBadge-'             + sId);
    if (prompt)  prompt.style.display  = 'none';
    if (toolbar) { toolbar.style.display = 'flex'; toolbar.style.justifyContent = 'center'; }
    if (label)   label.style.display   = 'block';
    if (badge && lastTier) badge.style.display = 'flex';
    if (frame) {
      frame.style.display = 'block';
      const dims = getEffectiveDims();
      frame.innerHTML = buildFlagSVG(dataURL, dims ? dims.w / dims.h : 2 / 3);
    }
    const wrap = g('sparkMockupWrap-' + sId);
    if (wrap) wrap.style.cssText = 'display:block; width:90%; max-width:480px; margin-left:auto; margin-right:auto; position:relative;';
  }

  function hideMockup() {
    const prompt  = g('sparkUploadPrompt-'  + sId);
    const frame   = g('sparkMockupFrame-'   + sId);
    const toolbar = g('sparkMockupToolbar-' + sId);
    const label   = g('sparkMockupLabel-'   + sId);
    if (prompt)  prompt.style.display  = 'flex';
    if (frame)   frame.style.display   = 'none';
    if (toolbar) toolbar.style.display = 'none';
    if (label)   label.style.display   = 'none';
  }

  function updateMockupLabel() {
    const el = g('sparkMockupLabel-' + sId), dims = getEffectiveDims();
    if (!el) return;
    el.textContent = dims
      ? (dims.h > dims.w ? '↕ Vertical' : '↔ Horizontal') + ' · ' + dims.w + ' × ' + dims.h + ' cm'
      : 'Preview of your flag';
  }

  // ── ROTATE ────────────────────────────────────────────────────────────────
  function onRotate() {
    if (processing || !lastURL || !storedW) return;
    setProcessing(true);
    dimsRotated = !dimsRotated;
    const dims = getEffectiveDims();
    if (!dims) { setProcessing(false); return; }
    reCropToRatio(dims).then(function (result) {
      generatePreviewAndCommit(result.canvas, result.cropData, calcDpi(result.cropData, dims), dpiTier(calcDpi(result.cropData, dims)), false);
      setProcessing(false);
    });
  }

  // ── BAKE ROTATION ─────────────────────────────────────────────────────────
  function bakeRotationAndReinit() {
    if (!cropperInst) return;
    const ccwBtn = g('cropRotateCCW-' + sId), cwBtn = g('cropRotateCW-' + sId);
    if (ccwBtn) ccwBtn.disabled = true; if (cwBtn) cwBtn.disabled = true;
    const rotateDeg = cropperInst.getData().rotate || 0;
    const radians   = rotateDeg * Math.PI / 180;
    const absCos = Math.abs(Math.cos(radians)), absSin = Math.abs(Math.sin(radians));
    const newW = Math.round(storedW * absCos + storedH * absSin);
    const newH = Math.round(storedW * absSin + storedH * absCos);
    const bakedCanvas = document.createElement('canvas');
    bakedCanvas.width = newW; bakedCanvas.height = newH;
    const ctx = bakedCanvas.getContext('2d');
    const srcImg = new Image();
    srcImg.onload = function () {
      ctx.translate(newW / 2, newH / 2); ctx.rotate(radians); ctx.translate(-storedW / 2, -storedH / 2);
      ctx.drawImage(srcImg, 0, 0);
      storedW = newW; storedH = newH; lastCropData = null;
      const dims = getEffectiveDims(), ratio = dims ? dims.w / dims.h : NaN;
      bakedCanvas.toBlob(function (blob) {
        const bakedURL = URL.createObjectURL(blob);
        cropperInst.destroy(); cropperInst = null;
        const cropImg = g('cropImg-' + sId);
        cropImg.onload = null; cropImg.src = '';
        cropImg.onload = function () {
          cropImg.onload = null;
          cropperInst = new Cropper(cropImg, {
            aspectRatio: ratio, viewMode: 1, dragMode: 'move',
            autoCropArea: 0.95, responsive: true, checkOrientation: false,
            ready: function () { injectCropperMarginOverlay(); if (ccwBtn) ccwBtn.disabled = false; if (cwBtn) cwBtn.disabled = false; },
            crop:  function () { updateCropperMarginOverlay(); }
          });
        };
        URL.revokeObjectURL(lastURL); lastURL = bakedURL; cropImg.src = bakedURL;
      }, 'image/jpeg', 0.95);
    };
    srcImg.src = lastURL;
  }

  // ── CROPPER MARGIN OVERLAY ─────────────────────────────────────────────────
  function injectCropperMarginOverlay() {
    if (!cropperInst) return;
    const overlay = g('sparkCropper-' + sId); if (!overlay) return;
    var old = overlay.querySelector('.spark-crop-margin-svg'); if (old) old.remove();
    var canvasWrap = overlay.querySelector('.spark-cropper-canvas-wrap'); if (!canvasWrap) return;
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('class', 'spark-crop-margin-svg');
    svg.style.cssText = 'position:absolute;top:0;left:0;width:0;height:0;pointer-events:none;z-index:10;overflow:visible;';
    var defs = document.createElementNS(ns, 'defs'), pattern = document.createElementNS(ns, 'pattern');
    var pid = 'scmPat-' + sId;
    pattern.setAttribute('id', pid); pattern.setAttribute('patternUnits', 'userSpaceOnUse');
    pattern.setAttribute('width', '10'); pattern.setAttribute('height', '10'); pattern.setAttribute('patternTransform', 'rotate(45)');
    var pr1 = document.createElementNS(ns, 'rect'); pr1.setAttribute('width','5'); pr1.setAttribute('height','10'); pr1.setAttribute('fill','rgba(210,30,30,0.72)');
    var pr2 = document.createElementNS(ns, 'rect'); pr2.setAttribute('x','5'); pr2.setAttribute('width','5'); pr2.setAttribute('height','10'); pr2.setAttribute('fill','rgba(210,30,30,0.18)');
    pattern.appendChild(pr1); pattern.appendChild(pr2); defs.appendChild(pattern); svg.appendChild(defs);
    ['scmTop','scmBot','scmLft','scmRgt'].forEach(function (id) {
      var r = document.createElementNS(ns, 'rect'); r.setAttribute('id', id + '-' + sId); r.setAttribute('fill', 'url(#' + pid + ')'); svg.appendChild(r);
    });
    canvasWrap.appendChild(svg);
    updateCropperMarginOverlay();
  }

  function updateCropperMarginOverlay() {
    if (!cropperInst) return;
    var overlay = g('sparkCropper-' + sId); if (!overlay) return;
    var svg = overlay.querySelector('.spark-crop-margin-svg'); if (!svg) return;
    var dims = getEffectiveDims(); if (!dims) { svg.style.display = 'none'; return; }
    svg.style.display = '';
    var cropBox; try { cropBox = cropperInst.getCropBoxData(); } catch (e) { return; }
    var bw = cropBox.width, bh = cropBox.height;
    svg.style.left = cropBox.left + 'px'; svg.style.top = cropBox.top + 'px';
    svg.style.width = bw + 'px'; svg.style.height = bh + 'px';
    svg.setAttribute('viewBox', '0 0 ' + bw + ' ' + bh); svg.setAttribute('width', bw); svg.setAttribute('height', bh);
    var mX = (MARGIN_CM / dims.w) * bw, mY = (MARGIN_CM / dims.h) * bh;
    var top = svg.querySelector('#scmTop-' + sId); if (top) { top.setAttribute('x',0); top.setAttribute('y',0); top.setAttribute('width',bw); top.setAttribute('height',mY); }
    var bot = svg.querySelector('#scmBot-' + sId); if (bot) { bot.setAttribute('x',0); bot.setAttribute('y', bh - mY); bot.setAttribute('width',bw); bot.setAttribute('height',mY); }
    var lft = svg.querySelector('#scmLft-' + sId); if (lft) { lft.setAttribute('x',0); lft.setAttribute('y',mY); lft.setAttribute('width',mX); lft.setAttribute('height', bh - mY * 2); }
    var rgt = svg.querySelector('#scmRgt-' + sId); if (rgt) { rgt.setAttribute('x', bw - mX); rgt.setAttribute('y',mY); rgt.setAttribute('width',mX); rgt.setAttribute('height', bh - mY * 2); }
  }

  // ── CROPPER MODAL ─────────────────────────────────────────────────────────
  function createCropperModal() {
    if (g('sparkCropper-' + sId)) return;
    const el = document.createElement('div');
    el.id = 'sparkCropper-' + sId; el.className = 'spark-cropper-overlay';
    el.innerHTML =
      '<div class="spark-cropper-modal" role="dialog" aria-modal="true">' +
        '<div class="spark-cropper-header">' +
          '<div><p class="spark-cropper-title">Adjust crop</p><p class="spark-cropper-subtitle">Drag to reposition · scroll or pinch to zoom</p></div>' +
          '<button class="spark-cropper-close" id="cropClose-' + sId + '" type="button">&#x2715;</button>' +
        '</div>' +
        '<div class="spark-cropper-canvas-wrap" style="position:relative;"><img id="cropImg-' + sId + '" src="" alt=""></div>' +
        '<div class="spark-crop-margin-legend" style="display:flex;align-items:center;gap:7px;padding:6px 16px 0;font-size:11.5px;color:#666;background:#1a1a1a;flex-shrink:0;">' +
          '<span style="display:inline-block;width:22px;height:10px;flex-shrink:0;border-radius:2px;background:repeating-linear-gradient(45deg,rgba(210,30,30,0.7) 0,rgba(210,30,30,0.7) 3px,rgba(210,30,30,0.15) 3px,rgba(210,30,30,0.15) 8px);"></span>' +
          '<span>Red zone (2 cm) will be tucked behind the hem</span>' +
        '</div>' +
        '<div class="spark-cropper-footer">' +
          '<div class="spark-cropper-toolbar">' +
            '<button class="spark-rotate-btn" id="cropRotateCCW-' + sId + '" type="button">↺ Rotate Left</button>' +
            '<button class="spark-rotate-btn" id="cropRotateCW-'  + sId + '" type="button">↻ Rotate Right</button>' +
          '</div>' +
          '<div class="spark-cropper-actions">' +
            '<button class="spark-cropper-btn spark-cropper-btn-cancel" id="cropCancel-' + sId + '" type="button">Cancel</button>' +
            '<button class="spark-cropper-btn spark-cropper-btn-apply"  id="cropApply-'  + sId + '" type="button">Apply Crop</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(el);
    el.addEventListener('click', function (e) { if (e.target === el) closeCropperModal(); });
    g('cropClose-'     + sId).addEventListener('click', closeCropperModal);
    g('cropCancel-'    + sId).addEventListener('click', closeCropperModal);
    g('cropApply-'     + sId).addEventListener('click', applyManualCrop);
    g('cropRotateCCW-' + sId).addEventListener('click', function () { if (cropperInst) { cropperInst.rotate(-90); bakeRotationAndReinit(); } });
    g('cropRotateCW-'  + sId).addEventListener('click', function () { if (cropperInst) { cropperInst.rotate(90);  bakeRotationAndReinit(); } });
  }

  function openCropperModal(objectURL, keepExisting) {
    createCropperModal();
    const overlay    = g('sparkCropper-' + sId);
    const img        = g('cropImg-'      + sId);
    const canvasWrap = overlay ? overlay.querySelector('.spark-cropper-canvas-wrap') : null;
    overlay.style.display = 'flex'; overlay.offsetHeight; overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    if (cropperInst) { cropperInst.destroy(); cropperInst = null; }
    const dims = getEffectiveDims(), ratio = dims ? dims.w / dims.h : NaN;
    if (canvasWrap && dims && dims.h > dims.w) {
      canvasWrap.style.maxWidth = Math.round(window.innerHeight * 0.55 * ratio) + 'px'; canvasWrap.style.margin = '0 auto';
    } else if (canvasWrap) { canvasWrap.style.maxWidth = ''; canvasWrap.style.margin = ''; }
    img.onload = null; img.src = '';
    img.onload = function () {
      img.onload = null;
      cropperInst = new Cropper(img, {
        aspectRatio: ratio, viewMode: 1, dragMode: 'move',
        autoCropArea: 0.95, responsive: true, checkOrientation: true,
        ready: function () { if (keepExisting && lastCropData) { try { cropperInst.setData(lastCropData); } catch (e) {} } injectCropperMarginOverlay(); },
        crop:  function () { updateCropperMarginOverlay(); }
      });
    };
    img.src = objectURL;
  }

  function closeCropperModal() {
    const overlay = g('sparkCropper-' + sId); if (!overlay) return;
    overlay.classList.remove('is-open'); overlay.style.display = 'none';
    document.body.style.overflow = '';
    if (cropperInst) { cropperInst.destroy(); cropperInst = null; }
  }

  function applyManualCrop() {
    if (!cropperInst) return;
    const cropData = cropperInst.getData(true); lastCropData = cropData;
    const dims = getEffectiveDims(), dpi = calcDpi(cropData, dims);
    const fullCanvas = cropperInst.getCroppedCanvas({ maxWidth: 4096, maxHeight: 4096, imageSmoothingQuality: 'high' });
    generatePreviewAndCommit(fullCanvas, cropData, dpi, dpiTier(dpi), false);
    closeCropperModal();
  }

  // ── COMMIT CROP ───────────────────────────────────────────────────────────
  function generatePreviewAndCommit(fullCanvas, cropData, dpi, tier, isFirstUpload) {
    var uploadDataURL = fullCanvas.toDataURL('image/jpeg', 0.92);
    var dims = getEffectiveDims(), previewCanvas = makeSafeZoneCanvas(fullCanvas, dims);
    croppedDataURL = previewCanvas.toDataURL('image/jpeg', 0.92);
    lastDpi = dpi; lastTier = tier; lastCropData = cropData;
    writeProps(cropData, dpi, tier);
    showMockup(croppedDataURL); updateMockupLabel(); renderBadge(dpi, tier);
    swapFileInputWithCrop(uploadDataURL);
    if (isFirstUpload) { runProgressAnimation(croppedDataURL); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    else { const si = g('staticImg-' + sId); if (si && si.style.display !== 'none') si.src = croppedDataURL; }
  }

  // ── FILE INPUT SWAP ───────────────────────────────────────────────────────
  function dataURLtoFile(dataURL, filename) {
    const arr = dataURL.split(','), mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]), u8 = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8[i] = bstr.charCodeAt(i);
    return new File([u8], filename, { type: mime });
  }

  function swapFileInputWithCrop(dataURL) {
    if (!dataURL || !originalFile) return;
    const input = g('file-upload-input-' + sId); if (!input) return;
    const base = (originalFile.name || 'photo').replace(/\.[^.]+$/, '');
    try { const dt = new DataTransfer(); dt.items.add(dataURLtoFile(dataURL, base + '_cropped.jpg')); input.files = dt.files; }
    catch (e) { console.warn('Spark: DataTransfer not supported.', e); }
  }

  function writeProps(cropData, dpi, tier) {
    const el = g('prop-quality-' + sId);
    if (el) el.value = 'Print Quality: ' + tierLabel(tier) + (dpi ? ' (' + Math.round(dpi) + ' DPI)' : '');
  }

  function clearProps() { const el = g('prop-quality-' + sId); if (el) el.value = ''; }

  // ── QUALITY BADGE ─────────────────────────────────────────────────────────
  function renderBadge(dpi, tier) {
    const badge = g('qBadge-' + sId); if (!badge) return;
    if (tier === 'excellent') {
      badge.className = 'quality-badge quality-excellent';
      badge.innerHTML = '<span class="quality-icon">✓</span><span class="quality-label">Great quality — your flag will look sharp</span>';
      badge.style.display = 'flex'; return;
    }
    if (tier === 'good') {
      badge.className = 'quality-badge quality-good';
      badge.innerHTML = '<span class="quality-icon">✓</span><span class="quality-label">Good quality — you\'re all set</span>';
      badge.style.display = 'flex'; return;
    }
    badge.className = 'quality-badge quality-verylow';
    badge.innerHTML =
      '<div class="qb-main-row"><span class="quality-icon">⚠</span><span class="quality-label">Low resolution — details may look blurry when printed.</span></div>' +
      '<div class="qb-actions-row">' +
        '<button class="qb-btn qb-btn-learn"   id="qbLearn-'   + sId + '" type="button">What can I do?</button>' +
        '<button class="qb-btn qb-btn-proceed" id="qbProceed-' + sId + '" type="button">Proceed anyway</button>' +
      '</div>';
    badge.style.display = 'flex';
    badge.querySelector('#qbLearn-'   + sId).addEventListener('click', openQualityModal);
    badge.querySelector('#qbProceed-' + sId).addEventListener('click', function () {
      badge.className = 'quality-badge quality-acknowledged';
      badge.innerHTML = '<span class="quality-icon" style="font-size:13px">ℹ</span><span style="font-size:12px;color:#888">Quality noted — we\'ll do our best</span>';
      badge.style.display = 'flex';
    });
  }

  // ── PROGRESS BAR ──────────────────────────────────────────────────────────
  function runProgressAnimation(finalURL) {
    const movingImg = g('movingImg-' + sId), barWrap = g('barWrap-' + sId);
    const mover = g('mover-' + sId), barFill = g('barFill-' + sId);
    if (!barWrap) return;
    movingImg.src = finalURL; barWrap.style.display = 'block'; mover.style.display = 'block';
    barFill.style.width = '0%'; mover.style.left = '0%';
    let progress = 0;
    const tick = setInterval(function () {
      progress = Math.min(progress + 4, 100);
      barFill.style.width = progress + '%'; mover.style.left = progress + '%';
      if (progress >= 100) {
        clearInterval(tick); barWrap.style.display = 'none'; mover.style.display = 'none';
        const fn = g('fName-' + sId); if (fn) fn.style.display = 'none';
      }
    }, 25);
  }

  // ── QUALITY MODAL ─────────────────────────────────────────────────────────
  function createQualityModal() {
    if (g('sparkModal-' + sId)) return;
    const el = document.createElement('div');
    el.id = 'sparkModal-' + sId; el.className = 'spark-modal-overlay';
    el.setAttribute('role', 'dialog'); el.setAttribute('aria-modal', 'true');
    el.innerHTML =
      '<div class="spark-modal">' +
        '<button class="spark-modal-close" type="button">&#x2715;</button>' +
        '<h4>About your image quality</h4>' +
        '<p class="spark-modal-subtitle">Here\'s what this means and what you can do.</p>' +
        '<div class="spark-modal-section"><div class="spark-modal-section-title">Why it\'s flagged</div>' +
        '<ul class="spark-modal-list"><li><span class="mi">📐</span><span>Your image\'s resolution is lower than ideal for the selected size.</span></li></ul></div>' +
        '<div class="spark-modal-section"><div class="spark-modal-section-title">Common causes</div>' +
        '<ul class="spark-modal-list">' +
          '<li><span class="mi">💬</span><span>Saved from WhatsApp or social media</span></li>' +
          '<li><span class="mi">📸</span><span>Screenshot instead of original file</span></li>' +
        '</ul></div>' +
        '<div class="spark-modal-section"><div class="spark-modal-section-title">What you can try</div>' +
        '<ul class="spark-modal-list">' +
          '<li><span class="mi">✅</span><span>Find the original in your camera roll</span></li>' +
          '<li><span class="mi">✅</span><span>Choose a smaller print size</span></li>' +
        '</ul></div>' +
        '<hr class="spark-modal-divider">' +
        '<p class="spark-modal-note">You can still proceed — our Zero Pixelation technology enhances every image before printing.</p>' +
      '</div>';
    document.body.appendChild(el);
    el.querySelector('.spark-modal-close').addEventListener('click', closeQualityModal);
    el.addEventListener('click', function (ev) { if (ev.target === el) closeQualityModal(); });
  }

  function openQualityModal() {
    createQualityModal();
    const o = g('sparkModal-' + sId);
    o.style.display = 'flex'; o.offsetHeight; o.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeQualityModal() {
    const o = g('sparkModal-' + sId); if (!o) return;
    o.classList.remove('is-open'); o.style.display = 'none'; document.body.style.overflow = '';
  }

  // ── RESET ─────────────────────────────────────────────────────────────────
  function resetAll() {
    const input = g('file-upload-input-' + sId);
    if (input) { try { input.files = new DataTransfer().files; } catch (e) {} input.value = ''; }
    if (lastURL) { URL.revokeObjectURL(lastURL); lastURL = null; }
    originalFile = null; croppedDataURL = null; lastDpi = null; lastTier = null;
    lastCropData = null; storedW = storedH = null; dimsRotated = false; processing = false;
    ['staticImg-', 'btn-remove-', 'qBadge-', 'barWrap-'].forEach(function (p) {
      const el = g(p + sId); if (el) el.style.display = 'none';
    });
    const si = g('staticImg-' + sId); if (si) si.src = '';
    const fn = g('fName-' + sId); if (fn) { fn.textContent = 'No file chosen'; fn.style.display = ''; }
    hideMockup(); clearProps(); closeQualityModal();
  }

  // ── INIT ──────────────────────────────────────────────────────────────────
  function initUploader() {
    const input = g('file-upload-input-' + sId);
    if (!input || input.dataset.sparkActive) return;
    input.dataset.sparkActive = 'true';
    const btnRemove = g('btn-remove-' + sId);
    if (btnRemove) btnRemove.addEventListener('click', resetAll);

    input.addEventListener('change', function (ev) {
      const file = ev.target.files[0]; if (!file) return;

      function startProcessing(sourceFile) {
        originalFile = sourceFile; dimsRotated = false; processing = false;
        if (lastURL) URL.revokeObjectURL(lastURL);
        lastURL = URL.createObjectURL(sourceFile);
        const tempImg = new Image();
        tempImg.onload = function () {
          storedW = tempImg.naturalWidth; storedH = tempImg.naturalHeight;
          setTimeout(function () { suggestAndSelectBestSize(storedW, storedH); }, 300);
          const rawDims = getPrintDims();
          if (rawDims) dimsRotated = (storedH > storedW) !== (rawDims.h > rawDims.w);
          const dims = getEffectiveDims();
          if (!dims) {
            var qb = g('qBadge-' + sId);
            if (qb) { qb.className = 'quality-badge quality-good'; qb.innerHTML = '<span class="quality-icon">✓</span><span class="quality-label">Photo uploaded — select a size above to see your preview</span>'; qb.style.display = 'flex'; }
            return;
          }
          autoCropToCanvas(storedW, storedH, dims.w / dims.h).then(function (result) {
            generatePreviewAndCommit(result.canvas, result.cropData, calcDpi(result.cropData, dims), dpiTier(calcDpi(result.cropData, dims)), true);
          });
        };
        tempImg.src = lastURL;
      }

      const isHEIC = file.type === 'image/heic' || file.type === 'image/heif' || /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name);
      if (isHEIC) {
        const objectURL = URL.createObjectURL(file);
        const img = new Image();
        img.onload = function () {
          URL.revokeObjectURL(objectURL);
          const canvas = document.createElement('canvas'); canvas.width = img.width; canvas.height = img.height;
          canvas.getContext('2d').drawImage(img, 0, 0);
          canvas.toBlob(function (pngBlob) { startProcessing(new File([pngBlob], file.name.replace(/\.[^.]+$/, '') + '.png', { type: 'image/png' })); }, 'image/png');
        };
        img.onerror = function () {
          URL.revokeObjectURL(objectURL);
          const isMac = /Mac/i.test(navigator.platform || navigator.userAgent);
          const isWin = /Win/i.test(navigator.platform || navigator.userAgent);
          let msg = 'Your photo is in HEIC format which this browser can\'t read.\n\n';
          if (isMac)      msg += 'On Mac: Open in Preview → File → Export → JPEG → Save.';
          else if (isWin) msg += 'On Windows: Open in Photos → ⋯ → Save a copy → JPEG.';
          else            msg += 'Please export your photo as JPEG and upload that instead.';
          alert(msg);
        };
        img.src = objectURL; return;
      }
      startProcessing(file);
    });
  }

  // ── VARIANT / SIZE CHANGE ─────────────────────────────────────────────────
  document.addEventListener('change', function (ev) {
    if (ev.target.id === 'file-upload-input-' + sId) return;
    if (!ev.target.matches('input[type="radio"], select')) return;
    if (!lastURL || !storedW) return;
    if (!croppedDataURL) {
      const rawDims = getPrintDims();
      if (rawDims && storedW && storedH) dimsRotated = (storedH > storedW) !== (rawDims.h > rawDims.w);
    }
    const dims = getEffectiveDims(); if (!dims) return;
    reCropToRatio(dims).then(function (result) {
      generatePreviewAndCommit(result.canvas, result.cropData, calcDpi(result.cropData, dims), dpiTier(calcDpi(result.cropData, dims)), false);
      setTimeout(function () { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 120);
    });
    if (cropperInst) cropperInst.setAspectRatio(dims.w / dims.h);
  });

  // ── BOOT ──────────────────────────────────────────────────────────────────
  function boot() {
    initUploader();
    var attempts = 0;
    function tryInject() {
      if (getGalleryEl()) { injectMockupPlaceholder(); }
      else if (attempts++ < 20) { setTimeout(tryInject, 100); }
    }
    tryInject();
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); }
  else { boot(); }
  if (window.Shopify && window.Shopify.designMode) { document.addEventListener('shopify:section:load', boot); }

  // ── ADD TO CART ────────────────────────────────────────────────────────────
  (function initAtcProgress() {

    // ── CONFIRM MODAL ────────────────────────────────────────────────────────
    function createConfirmModal() {
      if (g('sparkConfirm-' + sId)) return;
      var el = document.createElement('div');
      el.id = 'sparkConfirm-' + sId; el.className = 'spark-confirm-overlay';
      el.setAttribute('role', 'dialog'); el.setAttribute('aria-modal', 'true');
      el.innerHTML =
        '<div class="spark-confirm-modal">' +
          '<div class="spark-confirm-header">' +
            '<p class="spark-confirm-title">Does this look right?</p>' +
            '<p class="spark-confirm-subtitle">This is exactly how your flag will be cropped and produced.</p>' +
          '</div>' +
          '<div class="spark-confirm-preview-wrap">' +
            '<div class="spark-confirm-flag-wrap" id="sparkConfirmFlag-' + sId + '"></div>' +
            '<div id="sparkConfirmDims-'    + sId + '" class="spark-confirm-dims"    style="display:none;"></div>' +
            '<div id="sparkConfirmQuality-' + sId + '" class="spark-confirm-quality" style="display:none;"></div>' +
          '</div>' +
          '<div class="spark-confirm-actions">' +
            '<button class="spark-confirm-btn spark-confirm-btn-adjust"  id="sparkConfirmAdjust-'  + sId + '" type="button">' +
              '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>' +
              'Adjust crop' +
            '</button>' +
            '<button class="spark-confirm-btn spark-confirm-btn-proceed" id="sparkConfirmProceed-' + sId + '" type="button">' +
              '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' +
              'Looks perfect — add to cart' +
            '</button>' +
          '</div>' +
        '</div>';
      document.body.appendChild(el);
      el.addEventListener('click', function (e) { if (e.target === el) closeConfirmModal(); });
      g('sparkConfirmAdjust-' + sId).addEventListener('click', function () { closeConfirmModal(); openCropperModal(lastURL, true); });
    }

    function openConfirmModal(onProceed) {
      createConfirmModal();
      var el = g('sparkConfirm-' + sId);

      // Render flag SVG in the modal
      var flagEl = g('sparkConfirmFlag-' + sId);
      if (flagEl && croppedDataURL) {
        var dims = getEffectiveDims();
        flagEl.innerHTML = buildFlagSVG(croppedDataURL, dims ? dims.w / dims.h : 2 / 3);
      }

      // Dims label
      var dimsEl = g('sparkConfirmDims-' + sId);
      if (dimsEl) {
        var d = getEffectiveDims();
        if (d) { dimsEl.textContent = (d.h > d.w ? '↕ Vertical' : '↔ Horizontal') + ' · ' + d.w + ' × ' + d.h + ' cm'; dimsEl.style.display = 'block'; }
        else   { dimsEl.style.display = 'none'; }
      }

      // Quality pill
      var qualEl = g('sparkConfirmQuality-' + sId);
      if (qualEl) {
        if (lastTier === 'bad') {
          qualEl.innerHTML = '<span class="scq-icon">⚠</span><span>Low resolution — details may look blurry when printed</span>';
          qualEl.className = 'spark-confirm-quality scq-warn'; qualEl.style.display = 'flex';
        } else if (lastTier === 'excellent' || lastTier === 'good') {
          qualEl.innerHTML = '<span class="scq-icon">✓</span><span>' + (lastTier === 'excellent' ? 'Great quality' : 'Good quality') + ' — your flag will look sharp</span>';
          qualEl.className = 'spark-confirm-quality scq-good'; qualEl.style.display = 'flex';
        } else { qualEl.style.display = 'none'; }
      }

      // Re-wire proceed button fresh each open
      var oldBtn = g('sparkConfirmProceed-' + sId);
      if (oldBtn) {
        var newBtn = oldBtn.cloneNode(true);
        oldBtn.parentNode.replaceChild(newBtn, oldBtn);
        newBtn.addEventListener('click', function () { closeConfirmModal(); onProceed(); });
      }

      el.style.display = 'flex'; el.offsetHeight; el.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function closeConfirmModal() {
      var el = g('sparkConfirm-' + sId); if (!el) return;
      el.classList.remove('is-open'); el.style.display = 'none'; document.body.style.overflow = '';
    }

    // ── SHAKE upload button ─────────────────────────────────────────────────
    function shakeUploadBtn() {
      var uploadBtn = document.querySelector('label.spark-upload-btn[for="file-upload-input-' + sId + '"]');
      if (!uploadBtn) return;
      var count = 0;
      function doShake() {
        if (count++ >= 6) return;
        uploadBtn.classList.remove('shake'); void uploadBtn.offsetWidth; uploadBtn.classList.add('shake');
        setTimeout(function () { uploadBtn.classList.remove('shake'); setTimeout(doShake, 80); }, 500);
      }
      doShake();
    }

    function setup() {
      const form = document.getElementById('product-form-' + sId);
      if (!form || form.dataset.sparkAtc) return;
      form.dataset.sparkAtc = 'true';

      function getBtn() {
        return form.querySelector('[type="submit"]') ||
               document.querySelector('[form="product-form-' + sId + '"][type="submit"]');
      }

      var atcBar = document.getElementById('spark-atc-wrap-' + sId);
      var btn    = getBtn();
      if (atcBar && btn) { btn.parentNode.insertBefore(atcBar, btn); atcBar.style.marginTop = '0'; atcBar.style.marginBottom = '10px'; }

      function show(pct, msg) {
        const wrap = document.getElementById('spark-atc-wrap-' + sId);
        const fill = document.getElementById('spark-atc-fill-' + sId);
        const lbl  = document.getElementById('spark-atc-lbl-'  + sId);
        if (wrap) wrap.style.display = 'block';
        if (lbl && msg) lbl.textContent = msg;
        if (fill) {
          var w = Math.min(100, Math.round(pct)) + '%';
          fill.style.setProperty('width',     w,      'important');
          fill.style.setProperty('min-width', w,      'important');
          fill.style.setProperty('max-width', '100%', 'important');
          fill.style.setProperty('display',   'block', 'important');
        }
      }

      function hide() { const wrap = document.getElementById('spark-atc-wrap-' + sId); if (wrap) wrap.style.display = 'none'; }

      // Actual cart fetch — only called after confirmation
      function doAtcSubmit() {
        const btn = getBtn();
        if (btn && btn.dataset.sparkBusy) return;
        if (btn) { btn.disabled = true; btn.dataset.sparkBusy = '1'; }
        show(0, 'Uploading photo…');

        const fileInput     = g('file-upload-input-' + sId);
        const fileSizeBytes = fileInput && fileInput.files[0] ? fileInput.files[0].size : 0;
        const estimatedMs   = (fileSizeBytes / (400 * 1024)) * 1000;
        const startTime     = Date.now();

        let ticker = setInterval(function () {
          const elapsed = Date.now() - startTime;
          let pct = elapsed < estimatedMs
            ? (elapsed / estimatedMs) * 90
            : 90 + Math.min(9, ((elapsed - estimatedMs) / 10000) * 9);
          show(pct, pct < 30 ? 'Uploading photo…' : pct < 70 ? 'Uploading… ' + Math.round(pct) + '%' : pct < 90 ? 'Almost done… ' + Math.round(pct) + '%' : 'Adding to cart…');
        }, 200);

        fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin', body: new FormData(form) })
          .then(function (res) {
            if (!res.ok) return res.json().then(function (d) { throw new Error(d.description || d.message || 'Could not add to cart.'); });
            return res.json();
          })
          .then(function () {
            clearInterval(ticker); ticker = null;
            show(100, 'Added! Taking you to cart…');
            if (btn) { btn.disabled = false; delete btn.dataset.sparkBusy; }
            // ── PAGE REFRESH after short delay so cart header/drawer updates ──
            setTimeout(function () { window.location.reload(); }, 800);
          })
          .catch(function (err) {
            clearInterval(ticker); hide();
            if (btn) { btn.disabled = false; delete btn.dataset.sparkBusy; }
            alert(err.message || 'Something went wrong. Please try again.');
          });
      }

      // ── CAPTURE-PHASE submit listener ──────────────────────────────────────
      // Fires before all other handlers — always prevents default so nothing
      // else can trigger the cart. Cart only executes via doAtcSubmit().
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        ev.stopImmediatePropagation();

        var err = document.getElementById('spark-upload-err-' + sId);
        if (err) err.remove();

        const fileInput = g('file-upload-input-' + sId);
        const hasFile   = fileInput && fileInput.files && fileInput.files.length > 0;

        if (!hasFile) { shakeUploadBtn(); return; }

        openConfirmModal(doAtcSubmit);

      }, true); // <── capture phase
    }

    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', setup); }
    else { setup(); }
    if (window.Shopify && window.Shopify.designMode) { document.addEventListener('shopify:section:load', setup); }

  })();

})();